import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import type { OrderTextElement } from "@ai-teach/types";
import { FaMinus, FaPlus } from "react-icons/fa";

interface OrderTextElementToolbarProps {
  orderTextElement: OrderTextElement;
}

const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 48;
const FONT_SIZE_STEP = 2;
const DEFAULT_FONT_SIZE = 20;

/** Authoring control for the text ordering question's item row size (previously fixed at 14px). */
export function OrderTextElementToolbar({ orderTextElement }: OrderTextElementToolbarProps) {
  const dispatch = useAppDispatch();
  const fontSize = orderTextElement.fontSize ?? DEFAULT_FONT_SIZE;

  const setFontSize = (next: number) =>
    dispatch(
      updateElementProps({
        id: orderTextElement.id,
        props: { fontSize: Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, next)) },
      })
    );

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs font-semibold text-gray-500 uppercase">Text size</span>
      <button
        type="button"
        disabled={fontSize <= MIN_FONT_SIZE}
        onClick={() => setFontSize(fontSize - FONT_SIZE_STEP)}
        aria-label="Decrease text size"
        title="Decrease text size"
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-40"
      >
        <FaMinus className="w-3 h-3" />
      </button>
      <span className="text-xs font-medium text-gray-500 w-8 text-center select-none">
        {fontSize}
      </span>
      <button
        type="button"
        disabled={fontSize >= MAX_FONT_SIZE}
        onClick={() => setFontSize(fontSize + FONT_SIZE_STEP)}
        aria-label="Increase text size"
        title="Increase text size"
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-40"
      >
        <FaPlus className="w-3 h-3" />
      </button>
    </div>
  );
}
